import { useCallback, useMemo, useRef, useState } from 'react';
import { VariantCard } from './components';
import dayjs from '@root/lib/packages/dayjs';
import Slider from '@root/lib/components/Slider';
import ArrowRight from '@root/lib/components/Icons/ArrowRight';
import ArrowLeft from '@root/lib/components/Icons/ArrowLeft';
import useAsyncMemo from '@root/lib/hooks/useAsyncMemo';
import * as StorefrontApiService from '@root/lib/services/StorefrontApiService';
import './Selection.scss';

const sliderBreakpoints = [
  {
    width: 1024,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    width: 768,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    width: 470,
    settings: {
      slidesToShow: 1,
    },
  },
]

/**
 * Selection is a collection of products despite its singular name
 * 
 * @param {{
 *   deliveryDate: Date|dayjs.Dayjs
 *   items: Array<{
 *     quantity: number;
 *     boxProductId: number;
 *     variantId: number;
 *     productId: number;
 *   }>;
 * }} props
 */
const Selection = ({
  deliveryDate,
  items
}) => {
  const sliderRef = useRef();
  const [ sliderControlFlags, setSliderControlFlags ] = useState({next: false, prev: false});
  const itemCount = useMemo(() => {
    return items.reduce((a, c) => a + c.quantity, 0);
  }, [items]);
  const variants = useAsyncMemo(async () => {
    const variantIds = items.map(i => i.variantId);
    const variantMap = await StorefrontApiService.getVariantsById(variantIds, [
      'title',
      'id',
      'product', [
        'title',
        'tags',
      ],
      'image', [
        'alt: altText',
        'src: transformedSrc(maxWidth: 300)',
      ],
    ]);
    const variants = Array.from(variantMap.values())
      .filter(variant => !!variant)
      .map(variant => {
        const toNumericId = StorefrontApiService.converters.toNumericId;
        const item = items.find(item => item.variantId === toNumericId(variant.id))
        return {
          ...variant,
          quantity: item.quantity,
        };
      });

    return variants;
  }, [items]);

  /**
   * Handles scroll changes on the slider and enables/disables the slider directional
   * control buttons
   * 
   * @type {(next: boolean, prev: boolean) => void}
   */
  const handleScrollChange = useCallback((next, prev) => {
    setSliderControlFlags({next, prev});
  }, []);

  return (
    <div className="Selection">
      <div className="Selection__Container">
        <p className="Selection__Copy">
          You have <span className="Selection__ItemCount">{itemCount}</span> meal(s) <br class="mobile-fix" /> scheduled for delivery on:
          <br />
          <span className="Selection__DeliveryDate">
            {dayjs(deliveryDate ?? new Date()).tz().format('dddd, MMMM D')}
          </span>
          <br />
          After preparing your order, <br class="mobile-fix" /> we’ll email you a link <br class="mobile-fix" /> to track your delivery!
        </p>
        <div className="Selection__SliderContainer">
          {(sliderControlFlags.next || sliderControlFlags.prev) && (
            <div className="Selection__SliderControls">
              <button
                onClick={() => sliderRef.current.prev()}
                className="Selection__SliderControl Selection__SliderControl--left"
                disabled={!sliderControlFlags.prev}
                children={<ArrowLeft />}
              />
              <button
                onClick={() => sliderRef.current.next()}
                className="Selection__SliderControl Selection__SliderControl--right"
                disabled={!sliderControlFlags.next}
                children={<ArrowRight />}
              />
            </div>
          )}
          <Slider
            center
            className="Selection__Slider"
            slidesToShow={4}
            ref={sliderRef}
            onScrollChange={handleScrollChange}
            breakpoints={sliderBreakpoints}
          >
            {(variants.value ?? []).map(variant =>
              <VariantCard
                key={variant.id}
                {...variant}
              />
            )}
          </Slider>

        </div>
      </div>
    </div>
  );
}

export default Selection;